/**
 * Modal component - Flexible container with shadow styling
 * Factory function following the app's architectural patterns
 */

import "./Modal.scss"; // Co-located styles per repo standards [[memory:8094583]]

export type ModalAnchor =
  | "center"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center"
  | "left-center"
  | "right-center"
  | { x: number; y: number } // Custom position
  | { top?: number; right?: number; bottom?: number; left?: number }; // Direct CSS positioning

export interface ModalConfig {
  readonly className?: string;
  readonly onClose?: () => void;
  readonly closeOnBackdrop?: boolean;
  readonly showBackdrop?: boolean;
  readonly anchor?: ModalAnchor;
  readonly anchorElement?: HTMLElement; // Element to position relative to
  readonly offset?: { x: number; y: number }; // Offset from anchor point
  readonly width?: number | string; // Fixed width for modal container
}

export interface ModalAPI {
  readonly getElement: () => HTMLElement;
  readonly getContainer: () => HTMLElement;
  readonly appendChild: (child: HTMLElement) => void;
  readonly removeChild: (child: HTMLElement) => void;
  readonly show: () => void;
  readonly hide: () => void;
  readonly isVisible: () => boolean;
  readonly destroy: () => void;
}

/**
 * Creates Modal component with backdrop and container
 * @param config - Configuration for the modal
 * @returns Modal API
 */
export const createModal = (config: ModalConfig = {}): ModalAPI => {
  const {
    className = "",
    onClose,
    closeOnBackdrop = true,
    showBackdrop = true,
    anchor = "center",
    anchorElement,
    offset = { x: 0, y: 0 },
    width,
  } = config;

  let isDestroyed = false;
  let isVisible = false;
  let removeInputBlockers: () => void = () => {};
  let prevBodyOverflow: string | null = null;

  // Create backdrop element (optional)
  const backdrop = showBackdrop ? document.createElement("div") : null;
  if (backdrop) {
    backdrop.className = "modal-backdrop";
  }

  // Create modal container
  const container = document.createElement("div");
  container.className = `modal ${className}`.trim();
  if (typeof width !== "undefined") {
    container.style.width = typeof width === "number" ? `${width}px` : width;
  }

  // Create root element that contains both backdrop and modal
  const root = document.createElement("div");
  root.className = "modal-root";
  root.style.display = "none"; // Initially hidden

  // Append elements
  if (backdrop) {
    root.appendChild(backdrop);
  }
  root.appendChild(container);

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (event: MouseEvent): void => {
    if (isDestroyed || !closeOnBackdrop) return;

    // Only close if clicking the backdrop itself, not the modal content
    if (event.target === backdrop) {
      hide();
      onClose?.();
    }
  };

  /**
   * Handle escape key
   */
  const handleEscapeKey = (event: KeyboardEvent): void => {
    if (isDestroyed || !isVisible) return;

    if (event.key === "Escape") {
      hide();
      onClose?.();
    }
  };

  /**
   * Calculate modal position based on anchor
   */
  const calculatePosition = (): { x: number; y: number } => {
    if (typeof anchor === "object") {
      // Handle direct CSS positioning
      if (
        "top" in anchor ||
        "right" in anchor ||
        "bottom" in anchor ||
        "left" in anchor
      ) {
        return { x: 0, y: 0 }; // Will be handled by direct CSS positioning
      }
      // Handle x,y coordinates
      if ("x" in anchor && "y" in anchor) {
        return { x: anchor.x + offset.x, y: anchor.y + offset.y };
      }
    }

    if (anchorElement) {
      const rect = anchorElement.getBoundingClientRect();

      switch (anchor) {
        case "top-left":
          return { x: rect.left + offset.x, y: rect.top + offset.y };
        case "top-right":
          return { x: rect.right + offset.x, y: rect.top + offset.y };
        case "bottom-left":
          return { x: rect.left + offset.x, y: rect.bottom + offset.y };
        case "bottom-right":
          return { x: rect.right + offset.x, y: rect.bottom + offset.y };
        case "center":
        default:
          return {
            x: rect.left + rect.width / 2 + offset.x,
            y: rect.top + rect.height / 2 + offset.y,
          };
      }
    }

    // Default to viewport center
    return {
      x: window.innerWidth / 2 + offset.x,
      y: window.innerHeight / 2 + offset.y,
    };
  };

  /**
   * Position the modal
   */
  const positionModal = (): void => {
    // Handle direct CSS positioning
    if (
      typeof anchor === "object" &&
      ("top" in anchor ||
        "right" in anchor ||
        "bottom" in anchor ||
        "left" in anchor)
    ) {
      root.style.alignItems = "flex-start";
      root.style.justifyContent = "flex-start";
      container.style.position = "absolute";
      container.style.transform = "";

      // Clear all positioning
      container.style.top = "";
      container.style.right = "";
      container.style.bottom = "";
      container.style.left = "";

      // Apply direct positioning with offsets
      if (anchor.top !== undefined) {
        container.style.top = `${anchor.top + offset.y}px`;
      }
      if (anchor.right !== undefined) {
        container.style.right = `${anchor.right + offset.x}px`;
      }
      if (anchor.bottom !== undefined) {
        container.style.bottom = `${anchor.bottom + offset.y}px`;
      }
      if (anchor.left !== undefined) {
        container.style.left = `${anchor.left + offset.x}px`;
      }
      return;
    }

    if (anchor === "center" && !anchorElement) {
      // Use default flex centering
      root.style.alignItems = "center";
      root.style.justifyContent = "center";
      container.style.position = "relative";
      container.style.transform = "";
      return;
    }

    // Custom positioning with absolute positions (prefer top/bottom & left/right over transforms)
    const position = calculatePosition();

    root.style.alignItems = "flex-start";
    root.style.justifyContent = "flex-start";
    container.style.position = "absolute";

    // Clear previous explicit positions
    container.style.top = "";
    container.style.bottom = "";
    container.style.left = "";
    container.style.right = "";

    const isStringAnchor = typeof anchor === "string";
    const isBottomLeft = isStringAnchor && anchor === "bottom-left";
    const isBottomRight = isStringAnchor && anchor === "bottom-right";
    const isTopLeft = isStringAnchor && anchor === "top-left";
    const isTopRight = isStringAnchor && anchor === "top-right";
    const isTopCenter = isStringAnchor && anchor === "top-center";
    const isBottomCenter = isStringAnchor && anchor === "bottom-center";
    const isLeftCenter = isStringAnchor && anchor === "left-center";
    const isRightCenter = isStringAnchor && anchor === "right-center";

    if (anchorElement && isStringAnchor) {
      const rect = anchorElement.getBoundingClientRect();
      //

      // Horizontal placement (clear the opposite side explicitly)
      if (isTopRight || isBottomRight) {
        const vw = window.visualViewport?.width ?? window.innerWidth;
        container.style.right = `${Math.max(0, vw - rect.right + offset.x)}px`;
        container.style.left = "";
        //
      } else if (isTopLeft || isBottomLeft) {
        container.style.left = `${rect.left + offset.x}px`;
        container.style.right = "";
      } else if (isTopCenter || isBottomCenter) {
        // Center horizontally relative to anchor element
        const modalW = container.getBoundingClientRect().width;
        const centerX = rect.left + rect.width / 2;
        const leftValue = Math.max(0, centerX - modalW / 2 + offset.x);
        container.style.left = `${leftValue}px`;
        container.style.right = "";
      } else if (isLeftCenter) {
        container.style.left = `${rect.left + offset.x}px`;
        container.style.right = "";
      } else if (isRightCenter) {
        const vw = window.visualViewport?.width ?? window.innerWidth;
        container.style.right = `${Math.max(0, vw - rect.right + offset.x)}px`;
        container.style.left = "";
      }

      // Vertical placement using bottom semantics (no modal height in base math)
      {
        const vh = window.visualViewport?.height ?? window.innerHeight;
        if (isBottomLeft || isBottomRight || isBottomCenter) {
          let bottomValue = Math.max(0, vh - rect.bottom + offset.y);
          // Clamp so the modal's top stays within viewport inset (10px)
          const modalH = container.getBoundingClientRect().height;
          const maxBottom = Math.max(0, vh - modalH - 10);
          bottomValue = Math.min(bottomValue, maxBottom);
          container.style.bottom = `${bottomValue}px`;
          container.style.top = "";
          //
        } else if (isTopLeft || isTopRight || isTopCenter) {
          let bottomValue = Math.max(0, vh - rect.top + offset.y);
          const modalH = container.getBoundingClientRect().height;
          const maxBottom = Math.max(0, vh - modalH - 10);
          bottomValue = Math.min(bottomValue, maxBottom);
          container.style.bottom = `${bottomValue}px`;
          container.style.top = "";
          //
        } else if (isLeftCenter || isRightCenter) {
          // Vertically center relative to anchor element
          const modalH = container.getBoundingClientRect().height;
          const centerY = rect.top + rect.height / 2;
          const topValue = Math.max(0, centerY - modalH / 2 + offset.y);
          container.style.top = `${topValue}px`;
          container.style.bottom = "";
        }
      }
    } else if (anchor === "center") {
      // Viewport center (no anchorElement)
      container.style.left = `${position.x}px`;
      container.style.top = `${position.y}px`;
      container.style.transform = "translate(-50%, -50%)";
    } else if (isTopCenter || isBottomCenter || isLeftCenter || isRightCenter) {
      // Screen-edge centered anchors without anchorElement
      const vw = window.visualViewport?.width ?? window.innerWidth;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const modalW = container.getBoundingClientRect().width;
      const modalH = container.getBoundingClientRect().height;

      // Clear previous explicit positions
      container.style.top = "";
      container.style.bottom = "";
      container.style.left = "";
      container.style.right = "";
      container.style.transform = "";

      if (isBottomCenter) {
        const leftValue = Math.max(0, vw / 2 - modalW / 2 + offset.x);
        container.style.left = `${leftValue}px`;
        container.style.bottom = `${Math.max(0, offset.y)}px`;
      } else if (isTopCenter) {
        const leftValue = Math.max(0, vw / 2 - modalW / 2 + offset.x);
        container.style.left = `${leftValue}px`;
        container.style.top = `${Math.max(0, offset.y)}px`;
      } else if (isLeftCenter) {
        const topValue = Math.max(0, vh / 2 - modalH / 2 + offset.y);
        container.style.left = `${Math.max(0, offset.x)}px`;
        container.style.top = `${topValue}px`;
      } else if (isRightCenter) {
        const topValue = Math.max(0, vh / 2 - modalH / 2 + offset.y);
        container.style.right = `${Math.max(0, offset.x)}px`;
        container.style.top = `${topValue}px`;
      }
    } else {
      // For custom coordinates, align top-right by default
      container.style.left = `${position.x}px`;
      container.style.top = `${position.y}px`;
    }
    //

    // Remove transforms for edge anchors (top/bottom-left/right)
    if (
      isTopLeft ||
      isTopRight ||
      isBottomLeft ||
      isBottomRight ||
      isTopCenter ||
      isBottomCenter ||
      isLeftCenter ||
      isRightCenter
    ) {
      container.style.transform = "";
    }

    // Ensure modal stays within viewport
    const rect = container.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width - 10;
    const maxY = window.innerHeight - rect.height - 10;

    // Horizontal clamp
    if (
      typeof container.style.left === "string" &&
      container.style.left !== ""
    ) {
      if (rect.left < 10) container.style.left = "10px";
      else if (rect.left > maxX) container.style.left = `${maxX}px`;
    } else if (
      typeof container.style.right === "string" &&
      container.style.right !== ""
    ) {
      if (rect.right > window.innerWidth - 10) container.style.right = "10px";
      else if (rect.left < 10)
        container.style.right = `${window.innerWidth - (10 + rect.width)}px`;
    }

    // Vertical clamp
    if (typeof container.style.top === "string" && container.style.top !== "") {
      if (rect.top < 10) container.style.top = "10px";
      else if (rect.top > maxY) container.style.top = `${maxY}px`;
    } else if (
      typeof container.style.bottom === "string" &&
      container.style.bottom !== ""
    ) {
      if (rect.bottom > window.innerHeight - 10)
        container.style.bottom = "10px";
      else if (rect.top < 10)
        container.style.bottom = `${window.innerHeight - (10 + rect.height)}px`;
    }
  };

  /**
   * Block scroll/gesture events from reaching underlying layers while
   * allowing scrolling inside the modal content (no preventDefault).
   * This is important on iPad where nested scrollers can leak gestures.
   */
  const addInputBlockers = (): void => {
    const stopPropagation = (e: Event): void => {
      e.stopPropagation();
    };
    const options: AddEventListenerOptions = { capture: true, passive: false };

    // Capture common scroll/gesture event types
    root.addEventListener("wheel", stopPropagation, options);
    root.addEventListener("touchstart", stopPropagation, options);
    root.addEventListener("touchmove", stopPropagation, options);
    root.addEventListener("pointerdown", stopPropagation, options);
    root.addEventListener("pointermove", stopPropagation, options);

    removeInputBlockers = () => {
      root.removeEventListener("wheel", stopPropagation, options as any);
      root.removeEventListener("touchstart", stopPropagation, options as any);
      root.removeEventListener("touchmove", stopPropagation, options as any);
      root.removeEventListener("pointerdown", stopPropagation, options as any);
      root.removeEventListener("pointermove", stopPropagation, options as any);
      removeInputBlockers = () => {};
    };
  };

  /**
   * Show the modal
   */
  const show = (): void => {
    if (isDestroyed || isVisible) return;

    isVisible = true;
    root.style.display = "flex";

    // Position immediately to avoid timing issues in tests and UI flicker
    positionModal();
    // Re-position on next frame to account for layout changes/content size
    requestAnimationFrame(() => {
      positionModal();
    });

    // Add input blockers to prevent background scroll/gestures leaking
    addInputBlockers();

    // Lock body scroll to avoid iOS scroll chaining to the page
    prevBodyOverflow = document.body.style.overflow || null;
    document.body.style.overflow = "hidden";

    // Add event listeners
    if (backdrop && closeOnBackdrop) {
      backdrop.addEventListener("click", handleBackdropClick);
    }
    document.addEventListener("keydown", handleEscapeKey);

    // Focus management - focus the modal container
    container.focus();
  };

  /**
   * Hide the modal
   */
  const hide = (): void => {
    if (isDestroyed || !isVisible) return;

    isVisible = false;
    root.style.display = "none";

    // Remove event listeners
    if (backdrop) {
      backdrop.removeEventListener("click", handleBackdropClick);
    }
    document.removeEventListener("keydown", handleEscapeKey);

    // Remove input blockers
    removeInputBlockers();

    // Restore body scroll
    if (prevBodyOverflow !== null) {
      document.body.style.overflow = prevBodyOverflow;
      prevBodyOverflow = null;
    } else {
      document.body.style.overflow = "";
    }
  };

  /**
   * Add child element to modal container
   */
  const appendChild = (child: HTMLElement): void => {
    if (isDestroyed) return;
    container.appendChild(child);
  };

  /**
   * Remove child element from modal container
   */
  const removeChild = (child: HTMLElement): void => {
    if (isDestroyed) return;
    try {
      container.removeChild(child);
    } catch (error) {
      console.warn("Failed to remove child from modal:", error);
    }
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Remove event listeners
    if (backdrop) {
      backdrop.removeEventListener("click", handleBackdropClick);
    }
    document.removeEventListener("keydown", handleEscapeKey);

    // Remove from DOM if still connected
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  };

  // Set up initial focus management
  container.setAttribute("tabindex", "-1");

  return {
    getElement: () => root,
    getContainer: () => container,
    appendChild,
    removeChild,
    show,
    hide,
    isVisible: () => isVisible,
    destroy,
  };
};

/**
 * Creates Modal component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the modal
 * @returns Modal API
 */
export const createModalInContainer = (
  container: HTMLElement,
  config: ModalConfig = {}
): ModalAPI => {
  const modal = createModal(config);
  container.appendChild(modal.getElement());
  return modal;
};
